<template>
  <div class="modal">
    <div>
      <Button class="modalCloseButton" @click="updateIsMaterialUploadModalVisible">
        <v-ons-icon icon="fa-close" />
      </Button>
    </div>
    <div v-if="totalFileLength === 1">
      <p class="fontColor">以下のメディアをアップロードします</p>
      <div class="displayUploadImage">
        <img
          class="uploadImage"
          v-if="uploadingMaterialObject.materialUrl && uploadingMaterialObject.imageFlag"
          :src="uploadingMaterialObject.materialUrl"
          alt="uploading"
        />
        <video
          id="video"
          class="uploadImage"
          v-if="uploadingMaterialObject.src && !uploadingMaterialObject.imageFlag"
          preload="metadata"
          :src="uploadingMaterialObject.src"
          muted
          autoplay
          @playing="videoStop"
        />
      </div>
      <div>
        <p class="fontColor">ファイルサイズ：{{ uploadingMaterialObject.fileSize }} MB</p>
        <p class="fontColor" v-if="!uploadingMaterialObject.imageFlag">
          動画再生時間： {{ convertSecTommss(uploadingMaterialObject.duration) }}
        </p>
        <Button class="uploadButton" @click="materialUpload">アップロード</Button>
      </div>
      <div v-show="isAxiosWait" class="progress-circular-wrapper">
        <progress-circular />
      </div>
    </div>
    <div v-else>
      <div>
        <p class="fontColor">{{ totalFileLength }}件のメディアをアップロードします</p>
        <p class="fontColor">合計サイズ：{{ totalFileSize.toFixed(2) }} MB</p>
        <Button class="uploadButton" @click="materialUpload">アップロード</Button>
      </div>
      <div v-show="isAxiosWait" class="progress-circular-wrapper">
        <progress-circular />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';
import ProgressCircular from '@/components/common/ProgressCircular.vue';
import { getCurrentTime, isMovieExtension, createUuid } from '@/composable/common';
import { convertSecTommss } from '@/composable/material_common';
import { deleteFailureUploadMaterials, postMaterial } from '@/composable/data_push_common';
import { MATERIAL_UPLORD_STATUS } from '@/composable/constant_common';
import Button from '@/components/common/Button.vue';

const store = useStore();

const props = defineProps({
  updateIsMaterialUploadModalVisible: Function,
  uploadingMaterialObjectList: Array,
  uploadingMaterialObject: Object,
  getMaterial: Function,
  materialFileList: Array,
  totalFileLength: Number,
  totalFileSize: Number,
  resetPollingInterval: Function,
});

const isAxiosWait = ref(false);
const materialUrlList = ref([]);
const materialUplodingList = ref([]);

/**
 * materialStoreにアップロードするマテリアル情報を設定する処理
 */
const setMaterialUploadingList = () => {
  materialUrlList.value = []; // uuidを格納するリスト
  materialUplodingList.value = store.getters['materialStore/getMaterialUploadingList']; // storeの値を取得

  // 現在時刻を取得する
  const currentTime = getCurrentTime();
  // 変換前後の拡張子を定義する（変換前: 変換後）
  const extensionConversionMap = { webp: 'png' };

  // アップロード対象のファイル分繰り返す
  for (let i = 0; i < props.materialFileList.length; i += 1) {
    // uuidを発行して拡張子を付与する
    let extension = props.materialFileList[i].name.split('.').pop();
    if (isMovieExtension(extension)) {
      extension = 'mp4';
    }
    const uuid = createUuid();
    materialUrlList.value.push(`${uuid}.${extension}`);

    // アップロードされた素材の拡張子が変換対象の場合、対応する拡張子に変更しstoreに登録する
    if (Object.keys(extensionConversionMap).includes(extension.toLowerCase())) {
      extension = extensionConversionMap[extension.toLowerCase()];
    }

    // 1件ごとのデータをクラス変数に格納する
    materialUplodingList.value.push({
      materialUrl: `${uuid}.${extension}`,
      materialUploadStatus: MATERIAL_UPLORD_STATUS.MATERIAL_NOT_REGISTERED,
      createTime: currentTime,
    });
  }

  // materialStoreにマテリアルのアップロード情報を格納
  store.commit('materialStore/setMaterialUploadingList', materialUplodingList.value);
};

const materialUpload = async () => {
  if (isAxiosWait.value) return;

  // ポーリング解除
  props.resetPollingInterval();
  isAxiosWait.value = true;

  // アップロードに失敗したマテリアルの削除
  deleteFailureUploadMaterials();

  // materialStoreにアップロードするマテリアル情報を設定
  setMaterialUploadingList();

  // マテリアル登録処理呼び出し（非同期に実行）
  await postMaterial(
    props.uploadingMaterialObjectList,
    props.materialFileList,
    materialUrlList.value
  );

  // マテリアル一覧を取得、再表示
  props.getMaterial();
  props.updateIsMaterialUploadModalVisible();
  isAxiosWait.value = false;
};

const videoStop = () => {
  const video = document.getElementById('video');
  video.pause();
};
</script>

<style scoped>
.modal {
  background: #fff;
  border-radius: 10px;
  height: auto;
  margin: auto;
  overflow: scroll;
  max-width: 84rem;
  width: 90%;
  white-space: normal;
}
.modalCloseButton,
.modalCloseButton:active {
  background-color: #222222;
  border-radius: 50%;
  height: 3rem;
  padding: 0;
  position: absolute;
  right: -2%;
  top: -2%;
  vertical-align: middle;
  width: 3rem;
}
.fontColor {
  color: #222222;
  font-size: 1.8rem;
}
.displayUploadImage {
  width: 80%;
  max-height: 50vh;
  margin: auto;
  margin-bottom: 1rem;
}
.uploadImage {
  width: 100%;
  max-height: 50vh;
  object-fit: contain;
}
.uploadButton {
  background: #21963b;
  box-shadow: 0rem 0.2rem #13822c;
  border-radius: 10px;
  color: #fff;
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 2rem;
  text-align: center;
  vertical-align: middle;
  width: 80%;
  padding: 1rem 0;
}
.progress-circular-wrapper {
  background-color: rgba(0, 0, 0, 0.7);
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
}
</style>
